import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { validate } from 'uuid';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { URLS } from 'utils/tests/urls.config';
import { lendingApiFetchJson } from './lendingApi';
import { useQueryContext } from './lendingApiHooks';

const getApplicationsUrl = (
  introducerId: string,
  search?: string,
  dateFrom?: string | null,
) =>
  URLS.applications({ introducerId, query: { search, date_from: dateFrom } });

async function lendingAPIGetApplications(
  introducerId: string,
  dateFrom?: string | null,
  search?: string,
) {
  const response = await lendingApiFetchJson(
    getApplicationsUrl(introducerId, search, dateFrom),
  );
  return response;
}

export const useGetApplications = (
  search?: string,
  dateFrom?: string | null,
) => {
  const context = useQueryContext();
  const introducerId = context.data?.data.introducer_id;
  return useQuery({
    queryKey: ['applications', introducerId, dateFrom, search],
    queryFn: () =>
      lendingAPIGetApplications(introducerId as string, dateFrom, search),
    enabled: !!introducerId,
  });
};

async function lendingAPIGetApplication(
  introducerId: string,
  applicationId: string,
) {
  const isValid =
    applicationId &&
    typeof applicationId === 'string' &&
    validate(applicationId);

  if (!isValid) {
    throw Error;
  }

  const response = await lendingApiFetchJson(
    URLS.getApplication({ introducerId, applicationId }),
  );

  return response;
}

export const useGetApplication = (application_id?: string) => {
  const introducerContext = useQueryContext();
  const introducerId = introducerContext.data?.data.introducer_id;

  const router = useRouter();
  const { applicationId: routerApplicationId } = router.query;
  const applicationId = application_id || routerApplicationId;

  const query = useQuery({
    queryKey: ['application', introducerId, applicationId],
    queryFn: () =>
      lendingAPIGetApplication(introducerId as string, applicationId as string),
    enabled: !!introducerId && !!applicationId,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      router.push(PATH_NAMES.notFound());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);
  return query;
};
